import React, { useEffect } from "react";

type HubSpotFormProps = {
  portalId?: string;
  formId?: string;
  region?: string;
  fallbackUrl: string;
  fallbackLabel?: string;
  // When set, the visitor is redirected here after submitting the form —
  // this is the second step of the flow (the HubSpot meeting scheduler).
  redirectUrl?: string;
};

// HubSpot's current embed: a region-scoped loader script plus a
// `.hs-form-frame` div carrying the form's data attributes. The script scans
// the DOM (and watches for new frames) and renders each form into an iframe.
function embedScriptSrc(region: string, portalId: string) {
  return `https://js-${region}.hsforms.net/forms/embed/${portalId}.js`;
}

function ensureEmbedScript(region: string, portalId: string) {
  const src = embedScriptSrc(region, portalId);
  if (document.querySelector(`script[src=\"${src}\"]`)) {
    return;
  }
  const script = document.createElement("script");
  script.src = src;
  script.defer = true;
  document.body.appendChild(script);
}

function HubSpotForm({
  portalId,
  formId,
  region = "eu1",
  fallbackUrl,
  fallbackLabel = "Email us",
  redirectUrl,
}: HubSpotFormProps) {
  const hasConfig = Boolean(portalId?.trim() && formId?.trim());

  useEffect(() => {
    if (!hasConfig) {
      return;
    }
    ensureEmbedScript(region, portalId!.trim());
  }, [hasConfig, region, portalId]);

  // Move to the scheduler (step 2) once the form has been submitted. The
  // embedded form posts a message to the parent window on submit.
  useEffect(() => {
    const target = redirectUrl?.trim();
    if (!hasConfig || !target) {
      return undefined;
    }

    function handleMessage(event: MessageEvent) {
      const data = event.data as
        | { type?: string; eventName?: string; id?: string }
        | undefined;
      if (
        data?.type === "hsFormCallback" &&
        data?.eventName === "onFormSubmitted" &&
        (!data.id || data.id === formId)
      ) {
        window.location.href = target as string;
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [hasConfig, redirectUrl, formId]);

  if (!hasConfig) {
    return (
      <div className="registration-fallback">
        <a
          className="cta-button"
          href={fallbackUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {fallbackLabel}
        </a>
        <p className="cta-note">
          The booking form is being set up. Please use the email link to get in
          touch.
        </p>
      </div>
    );
  }

  return (
    <div className="registration-form-shell">
      <div
        className="hs-form-frame hubspot-form-target"
        data-region={region}
        data-form-id={formId}
        data-portal-id={portalId}
      />
    </div>
  );
}

export default HubSpotForm;
